import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { bankPaymentsAvailable } from '../components/PayInvoicesModal';
import invoiceService from '../services/invoiceService';
import clinicService from '../services/clinicService';
import { useAuth } from '../context/AuthContext';
import { isAdmin } from '../utils/roles';
import useDebounced from '../hooks/useDebounced';
import { useT } from '../i18n/LanguageContext';
import { requestErrorMessage } from '../utils/requestError';
import { captureSession, isSessionCurrent, SESSION_EVENT, SESSION_KEY } from '../utils/authSession';
import { invoicePdfFilename, saveInvoicePdf } from '../utils/invoicePdfDownload';
import {
  PAGE_SIZE, pdfAvailable, payable, SORT_DEFAULT_DIR, TABS,
} from '../features/invoices/invoiceLedger';
import { canSelectInvoice, selectedBalanceTotal, selectedPayableRows, selectionForClinic } from '../features/invoices/invoiceSelection';
import useAccessibleTabs from '../hooks/useAccessibleTabs';
import InvoiceLedgerView from '../features/invoices/InvoiceLedgerView';

const pdfAccessKey = (user) => JSON.stringify([
  user?._id || user?.id || '', user?.role || '', user?.clinicRole || '',
  user?.clinic?._id || user?.clinic || '',
  (Array.isArray(user?.clinics) ? user.clinics : []).map(c => String(c?._id || c)).sort(),
]);
const storedPdfAccessKey = () => {
  try { return pdfAccessKey(JSON.parse(localStorage.getItem('user'))); } catch { return ''; }
};

const isRestrictedClinicUser = (user) => user?.role === 'clinic' && String(user?.clinicRole || 'owner').trim().toLowerCase() !== 'owner';

export default function Invoices() {
  const t = useT();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const isClinicUser = user?.role === 'clinic';
  const restricted = isRestrictedClinicUser(user);
  const [activeTab, setActiveTab] = useState('all');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, outstanding: 0, summary: null, sync: null, page: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  // Sort travels to the server, so the order holds across every page, not just this one.
  const [sort, setSort] = useState({ key: 'txnDate', dir: 'desc' });
  const [loading, setLoading] = useState(true);
  /** The first load shows a spinner; every later reload keeps the table in place and dims it. */
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [retryingId, setRetryingId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search, 300);
  const [dates, setDates] = useState({ dateFrom: '', dateTo: '' });
  const [clinics, setClinics] = useState([]);
  const [clinicId, setClinicId] = useState('');
  // Paying: the ticked rows, the dialog (with the rows and clinic frozen at the moment it opened),
  // and what the browser needs to tokenize.
  const [selected, setSelected] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const pdfFlight = useRef(null);
  const mounted = useRef(true);
  const pdfAccess = pdfAccessKey(user);
  const pdfView = JSON.stringify([activeTab, search, dates.dateFrom, dates.dateTo, clinicId, page, sort.key, sort.dir]);
  const currentPdfContext = useRef({ access: pdfAccess, view: pdfView });
  currentPdfContext.current = { access: pdfAccess, view: pdfView };
  const [payCtx, setPayCtx] = useState(null);
  const [payConfig, setPayConfig] = useState(null);
  const [paidBanner, setPaidBanner] = useState(null);
  // Payments taken in the app, shown as a section under the Paid view.
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [workingId, setWorkingId] = useState(null);
  // Out-of-order responses: only the latest request may write the list.
  const seq = useRef(0);

  const cancelPdfDownload = useCallback(() => {
    pdfFlight.current?.controller.abort();
    pdfFlight.current = null;
    if (mounted.current) setDownloadingPdf(false);
  }, []);

  useEffect(() => {
    mounted.current = true;
    const cancel = () => { cancelPdfDownload(); setPdfError(''); };
    const storageChanged = (event) => {
      if (event.storageArea && event.storageArea !== localStorage) return;
      const flight = pdfFlight.current;
      if (flight) {
        if (!isSessionCurrent(flight.session) || storedPdfAccessKey() !== flight.storedAccess) cancel();
        return;
      }
      const accessValue = (value) => { try { return pdfAccessKey(JSON.parse(value)); } catch { return ''; } };
      if (event.key === null || event.key === SESSION_KEY ||
          (event.key === 'accessToken' && !event.newValue) ||
          (event.key === 'user' && accessValue(event.oldValue) !== accessValue(event.newValue))) cancel();
    };
    window.addEventListener(SESSION_EVENT, cancel);
    window.addEventListener('storage', storageChanged);
    return () => {
      mounted.current = false;
      cancelPdfDownload();
      window.removeEventListener(SESSION_EVENT, cancel);
      window.removeEventListener('storage', storageChanged);
    };
  }, [cancelPdfDownload]);

  useEffect(() => {
    cancelPdfDownload();
    setPdfError('');
  }, [pdfView, pdfAccess, cancelPdfDownload]);

  useEffect(() => {
    if (isClinicUser) return;
    clinicService
      .getClinics()
      .then((res) => {
        const list = res.data?.data?.data || res.data?.data || [];
        setClinics(Array.isArray(list) ? list : []);
      })
      .catch(() => setClinics([]));
  }, [isClinicUser]);

  useEffect(() => {
    invoiceService
      .getPayConfig()
      .then((res) => setPayConfig(res.data?.data || res.data || null))
      .catch(() => setPayConfig(null));
  }, []);

  // Any change of what is listed, or how it is ordered, starts from page 1.
  useEffect(() => {
    setPage(1);
  }, [activeTab, debouncedSearch, dates, clinicId, sort]);

  const fetchData = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: PAGE_SIZE, sort: sort.key, dir: sort.dir };
      if (activeTab !== 'all') params.status = activeTab;
      if (debouncedSearch) params.search = debouncedSearch;
      if (dates.dateFrom) params.dateFrom = dates.dateFrom;
      if (dates.dateTo) params.dateTo = dates.dateTo;
      if (clinicId) params.clinicId = clinicId;
      const res = await invoiceService.getLedger(params);
      if (mine !== seq.current) return;
      const payload = res.data?.data || {};
      const list = Array.isArray(payload.data) ? payload.data : [];
      setRows(list);
      setMeta({
        total: payload.total ?? 0,
        outstanding: payload.outstanding ?? 0,
        summary: payload.summary || null,
        sync: payload.sync || null,
        page: payload.page ?? 1,
        totalPages: payload.totalPages ?? 1,
      });
      // Prune the selection to what is on screen and still payable.
      setSelected((prev) => {
        const next = {};
        list.forEach((r) => { if (prev[r._id] && payable(r)) next[r._id] = true; });
        return next;
      });
    } catch (err) {
      if (mine !== seq.current) return;
      setError(requestErrorMessage(err, 'Could not load invoices.'));
      setRows([]);
    } finally {
      if (mine === seq.current) {
        setLoading(false);
        setLoaded(true);
      }
    }
  }, [activeTab, debouncedSearch, dates, clinicId, page, sort]);

  // Payments taken through MedEffects belong with the paid invoices: the Paid view carries them
  // as a section beneath the table, only once there is at least one.
  const fetchPayments = useCallback(async () => {
    if (activeTab !== 'paid') return;
    setPaymentsLoading(true);
    setError('');
    try {
      const params = { limit: 200 };
      if (clinicId) params.clinicId = clinicId;
      // Newest 200, plus - for an admin - every payment still waiting on its QuickBooks record,
      // however old: the Teams card sends them here to retry it, so it must never scroll off.
      const [res, pending] = await Promise.all([
        invoiceService.getPayments(params),
        admin ? invoiceService.getPayments({ ...params, status: 'paid_unrecorded', limit: 50 }) : Promise.resolve(null),
      ]);
      const rows = Array.isArray(res.data?.data?.data) ? res.data.data.data : [];
      const seen = new Set(rows.map((r) => r._id));
      for (const r of pending?.data?.data?.data || []) if (!seen.has(r._id)) rows.push(r);
      setPayments(rows);
    } catch (err) {
      setError(requestErrorMessage(err, 'Could not load payments.'));
      setPayments([]);
    } finally {
      setPaymentsLoading(false);
    }
  }, [activeTab, clinicId, admin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // ---- selection --------------------------------------------------------------------------------
  const selectedRows = useMemo(() => selectedPayableRows(rows, selected), [rows, selected]);
  const selectedClinicId = selectedRows[0]?.clinic?._id ? String(selectedRows[0].clinic._id) : '';
  const selectedClinicName = selectedRows[0]?.clinic?.name || '';
  const selectedTotal = selectedBalanceTotal(selectedRows);

  const toggle = (inv) => {
    if (pdfFlight.current) return;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[inv._id]) delete next[inv._id];
      else next[inv._id] = true;
      return next;
    });
    setPaidBanner(null);
  };

  // A ticked box is always untickable; an unticked box waits while another clinic is being paid.
  const canTick = (inv) => canSelectInvoice(inv, selected, selectedClinicId);

  // Bank is the only method: paying is on only when the server says so AND lists bank (or lists nothing).
  const paymentsOn = bankPaymentsAvailable(payConfig);
  const payableOnScreen = rows.filter(payable);
  // "Select all open" only when the CLINIC is already clear from context - a ticked invoice, the
  // clinic filter, or a practice login that only has one clinic. It never picks a clinic on its own.
  const contextClinicId = selectedClinicId || (clinicId ? String(clinicId) : '') || (isClinicUser && payableOnScreen[0]?.clinic?._id ? String(payableOnScreen[0].clinic._id) : '');
  const selectAllCandidates = payableOnScreen.filter((r) => String(r.clinic._id) === contextClinicId);
  const selectAllTargetName = selectedClinicName || clinics.find((c) => String(c._id) === contextClinicId)?.name || selectAllCandidates[0]?.clinic?.name || '';
  const showSelectAll = paymentsOn && !!contextClinicId && selectAllCandidates.length > selectedRows.length;
  const selectAllLabel = isClinicUser ? t('Select all open invoices') : t('Select all open invoices for {clinic}', { clinic: selectAllTargetName });
  const selectAllForClinic = () => {
    if (!contextClinicId || pdfFlight.current) return;
    setSelected(selectionForClinic(selectAllCandidates, contextClinicId));
  };

  const openPay = () => {
    if (!selectedRows.length || pdfFlight.current) return;
    setPayCtx({ invoices: selectedRows, clinicName: selectedClinicName });
  };

  const pdfReady = !loading && search === debouncedSearch && !restricted;
  const downloadPdf = async (invoices) => {
    if (!pdfReady || pdfFlight.current || payCtx || !invoices.length || invoices.length > PAGE_SIZE || !invoices.every(pdfAvailable)) return;
    const flight = {
      controller: new AbortController(), session: captureSession(),
      invoiceIds: invoices.map(invoice => invoice._id),
      access: pdfAccess, storedAccess: storedPdfAccessKey(), view: pdfView,
    };
    pdfFlight.current = flight;
    const isCurrent = () => mounted.current && pdfFlight.current === flight && !flight.controller.signal.aborted &&
      isSessionCurrent(flight.session) && currentPdfContext.current.access === flight.access &&
      currentPdfContext.current.view === flight.view && storedPdfAccessKey() === flight.storedAccess;
    setDownloadingPdf(true);
    setPdfError('');
    try {
      const response = await invoiceService.downloadPdf(flight.invoiceIds, {
        signal: flight.controller.signal, sessionBinding: flight.session,
      });
      if (!isCurrent()) return;
      await saveInvoicePdf(response, invoicePdfFilename(invoices), { isCurrent });
    } catch (error) {
      if (!isCurrent()) return;
      const multiple = flight.invoiceIds.length > 1;
      const message = error?.response?.status === 413
        ? (multiple ? 'Select fewer invoices and try again.' : 'This invoice PDF is too large to download. Contact MedEffects for help.')
        : error?.response?.status === 404
          ? (multiple ? 'One or more invoice PDFs are unavailable. Refresh the invoices and try again.' : 'This invoice PDF is unavailable. Refresh the invoices and try again.')
          : (multiple ? 'Could not download invoice PDFs. Please try again.' : 'Could not download the invoice PDF. Please try again.');
      setPdfError(requestErrorMessage(error, message));
    } finally {
      if (pdfFlight.current === flight) {
        pdfFlight.current = null;
        if (mounted.current) setDownloadingPdf(false);
      }
    }
  };

  const handlePaid = (receipt) => {
    setSelected({});
    setPaidBanner(receipt);
  };

  // `halted`: the dialog stopped after an outcome the server could not confirm. That selection must
  // not be offered for paying again - the server is finding out what happened to it.
  const closePay = (info) => {
    setPayCtx(null);
    if (info?.halted) setSelected({});
    fetchData();
    fetchPayments();
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await invoiceService.syncFromQuickBooks();
      await fetchData();
    } catch (err) {
      setError(requestErrorMessage(err, 'Sync from QuickBooks failed.'));
    } finally {
      setSyncing(false);
    }
  };

  const handleRetry = async (id) => {
    setRetryingId(id);
    try {
      await invoiceService.retry(id);
      await fetchData();
    } catch (err) {
      setError(requestErrorMessage(err, 'Retry failed.'));
    } finally {
      setRetryingId(null);
    }
  };

  const handlePaymentAction = async (p, action) => {
    setWorkingId(p._id);
    setError('');
    try {
      if (action === 'record') await invoiceService.retryRecord(p._id);
      else await invoiceService.refreshPayment(p._id);
      await fetchPayments();
    } catch (err) {
      setError(requestErrorMessage(err, 'That did not work.'));
    } finally {
      setWorkingId(null);
    }
  };

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      // First click on a column: dates newest first, amounts highest first, text A to Z.
      return { key, dir: SORT_DEFAULT_DIR[key] || 'asc' };
    });
  };

  const tabs = TABS.filter((t) => admin || !t.adminOnly);
  const invoiceTabKeys = tabs.map((tab) => tab.key);
  const { tabListProps, getTabProps, panelProps } = useAccessibleTabs({
    tabs: invoiceTabKeys,
    activeTab,
    onSelect: setActiveTab,
    label: t('Invoice views'),
  });
  const sandbox = payConfig?.environment === 'sandbox';
  // Flexible tracks carry a px minimum and the fixed columns are px, so the header grid and the
  // row grids resolve to identical widths - see utils/tableColumns and tableColumnAlignment.test.
  const COLUMNS = 'grid-cols-[28px_minmax(84px,2.2fr)_minmax(116px,3.12fr)_minmax(76px,2.1fr)_minmax(76px,2.1fr)_minmax(72px,2fr)_minmax(72px,2fr)_minmax(100px,2.4fr)_160px]';
  const columns = ['', 'INVOICE #', 'CLINIC', 'DATE', 'DUE', 'AMOUNT', 'BALANCE', 'STATUS', 'ACTIONS'];

  const from = meta.total === 0 ? 0 : (meta.page - 1) * PAGE_SIZE + 1;
  const to = Math.min(meta.total, meta.page * PAGE_SIZE);
  const summary = meta.summary;
  const syncFailed = !!meta.sync?.lastError;
  // "all clinics" is only true for an admin; a rep's figures cover the clinics assigned to them.
  const scopeLabel = clinicId ? clinics.find((c) => String(c._id) === String(clinicId))?.name || t('this clinic') : isClinicUser ? t('your clinic') : admin ? t('all clinics') : t('your clinics');

  return (
    <InvoiceLedgerView
      {...{
    t, restricted, admin, isClinicUser, search, setSearch,
    dates, setDates, handleSync, syncing, openPay, selectedRows,
    paymentsOn, downloadingPdf, selectedClinicName, selectedTotal, pdfError, sandbox,
    payConfig, paidBanner, setPaidBanner, payCtx, closePay, handlePaid,
    setSelected, fetchData, summary, scopeLabel, syncFailed, meta,
    tabs, tabListProps, getTabProps, setActiveTab, activeTab, clinicId,
    setClinicId, clinics, error, panelProps, loaded, loading,
    showSelectAll, selectAllTargetName, downloadPdf, pdfReady, selectAllForClinic, selectAllCandidates,
    selectAllLabel, pdfFlight, rows, COLUMNS, columns, sort,
    toggleSort, selected, canTick, toggle, retryingId, handleRetry,
    from, to, setPage, page, payments, paymentsLoading,
    workingId, handlePaymentAction,
      }}
    />
  );
}
